// Flooring fills/hatches — one look per floor type so all four read apart on the
// plan: pink / purple tile grids for tiled floors, an AR-CONC concrete stipple
// for a flat screed and a grout stipple for a sloping screed. Shared by the model
// plan (render.ts) and the paper-sheet flooring schedule swatch (titleBlock.ts),
// so a floor prints the same hatch it draws.
import type { FloorKind } from '../shared/types';

export type FloorHatch = 'grid' | 'concrete' | 'grout';
/** The hatch's pitch in world mm: the tile grid's, the concrete's 45° lines'. */
export const FLOOR_HATCH_MM = 250;
export const FLOOR_STYLE: Record<FloorKind, { fill: string; line: string; stroke: string; hatch: FloorHatch }> = {
  'tiled-flat': { fill: 'rgba(224, 90, 153, 0.14)', line: 'rgba(224, 90, 153, 0.62)', stroke: 'rgba(224, 90, 153, 0.95)', hatch: 'grid' },
  'tiled-slope': { fill: 'rgba(139, 92, 246, 0.14)', line: 'rgba(139, 92, 246, 0.62)', stroke: 'rgba(139, 92, 246, 0.95)', hatch: 'grid' },
  'screed-flat': { fill: 'rgba(90, 169, 230, 0.12)', line: 'rgba(90, 169, 230, 0.7)', stroke: 'rgba(90, 169, 230, 0.95)', hatch: 'concrete' },
  'screed-slope': { fill: 'rgba(46, 125, 91, 0.14)', line: 'rgba(46, 125, 91, 0.8)', stroke: 'rgba(46, 125, 91, 0.98)', hatch: 'grout' },
};

/** Deterministic [0,1) hash of an integer cell — a stable "random" so the
 *  concrete/grout stipples don't flicker frame to frame. */
export function cellHash(i: number, j: number): number {
  const s = Math.sin(i * 127.1 + j * 311.7) * 43758.5453;
  return s - Math.floor(s);
}

/** Upper bound on stipple cells across a hatch, so a huge floor at a coarse
 *  zoom can't emit hundreds of thousands of marks and stall the frame. When the
 *  grid would exceed this the cell size is grown to fit — the look thins out but
 *  stays a stipple, and never freezes. */
export const HATCH_MAX_CELLS = 20000;
export function stippleCell(base: number, minX: number, minY: number, maxX: number, maxY: number): number {
  const cols = (maxX - minX) / base, rows = (maxY - minY) / base;
  const n = cols * rows;
  return n > HATCH_MAX_CELLS ? base * Math.sqrt(n / HATCH_MAX_CELLS) : base;
}

/** The stipple's cell for a hatch of pitch `step` over a box, in the box's units. */
function stippleCellOf(hatch: 'concrete' | 'grout', minX: number, minY: number, maxX: number, maxY: number, step: number): number {
  return hatch === 'concrete'
    ? stippleCell(Math.max(8, step * 0.7), minX, minY, maxX, maxY)
    : stippleCell(Math.max(5, step * 0.32), minX, minY, maxX, maxY);
}

/** Each mark of a stipple over a box, in the box's own units (`step` the hatch's
 *  pitch in them): one per cell of its grid (`stippleCellOf`), jittered inside
 *  the cell by `cellHash` — the concrete's aggregate a dot (`dash` null) or a
 *  short dash at angle `dash`, the grout a dot. The ONE stipple rule: the canvas
 *  visits it in screen px, the DXF in world mm. */
export function eachStippleMark(
  hatch: 'concrete' | 'grout', minX: number, minY: number, maxX: number, maxY: number, step: number,
  mark: (x: number, y: number, dash: number | null) => void,
): void {
  const cell = stippleCellOf(hatch, minX, minY, maxX, maxY, step);
  for (let gi = Math.floor(minX / cell); gi <= Math.ceil(maxX / cell); gi++) {
    for (let gj = Math.floor(minY / cell); gj <= Math.ceil(maxY / cell); gj++) {
      if (hatch === 'grout') { mark(gi * cell + cellHash(gi, gj) * cell, gj * cell + cellHash(gj + 7, gi + 3) * cell, null); continue; }
      const h = cellHash(gi, gj);
      mark(gi * cell + h * cell * 0.7, gj * cell + cellHash(gj, gi) * cell * 0.7, h < 0.5 ? null : h * Math.PI);
    }
  }
}

/** AR-CONC: sparse 45° lines with scattered aggregate marks (dots + short dashes).
 *  Every dot is collected into ONE path and filled once, every dash into ONE path
 *  and stroked once — a big floor was emitting tens of thousands of separate
 *  beginPath/fill calls per frame otherwise. */
export function hatchConcrete(ctx: CanvasRenderingContext2D, minX: number, minY: number, maxX: number, maxY: number, step: number, color: string): void {
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let d = Math.floor((minX - maxY) / step) * step; d <= maxX - minY; d += step) { ctx.moveTo(minX, minX - d); ctx.lineTo(maxX, maxX - d); }
  ctx.stroke();
  const dots = new Path2D(), dashes = new Path2D();
  eachStippleMark('concrete', minX, minY, maxX, maxY, step, (cx, cy, a) => {
    if (a == null) { dots.moveTo(cx + 1.1, cy); dots.arc(cx, cy, 1.1, 0, Math.PI * 2); } // aggregate dot
    else { dashes.moveTo(cx, cy); dashes.lineTo(cx + Math.cos(a) * 4, cy + Math.sin(a) * 4); } // short dash
  });
  ctx.fill(dots);
  ctx.stroke(dashes);
}

/** Grout / mortar: a dense fine dot stipple. All dots go into ONE path filled
 *  once (per-dot fill() was the screed-slope's frame-rate sink), and the cell is
 *  capped so a large area can't spawn an unbounded number of them. */
export function hatchGrout(ctx: CanvasRenderingContext2D, minX: number, minY: number, maxX: number, maxY: number, step: number, color: string): void {
  ctx.fillStyle = color;
  const dots = new Path2D();
  eachStippleMark('grout', minX, minY, maxX, maxY, step, (cx, cy) => { dots.moveTo(cx + 0.9, cy); dots.arc(cx, cy, 0.9, 0, Math.PI * 2); });
  ctx.fill(dots);
}

/** A small floor-hatch swatch (used in the flooring schedule in place of a code,
 *  in both the model view and the paper sheet): the type's tint + hatch + outline
 *  in a box centred in the given cell. */
export function drawFloorSwatch(ctx: CanvasRenderingContext2D, cx: number, cy: number, cw: number, ch: number, kind: FloorKind): void {
  const style = FLOOR_STYLE[kind];
  const s = Math.max(10, Math.min(cw - 10, ch - 8));
  const x = cx + (cw - s) / 2, y = cy + (ch - s) / 2;
  ctx.save();
  ctx.beginPath(); ctx.rect(x, y, s, s);
  ctx.fillStyle = style.fill; ctx.fill();
  ctx.save();
  ctx.beginPath(); ctx.rect(x, y, s, s); ctx.clip();
  const step = 6;
  if (style.hatch === 'grid') {
    ctx.strokeStyle = style.line; ctx.lineWidth = 1; ctx.beginPath();
    for (let gx = x; gx <= x + s; gx += step) { ctx.moveTo(gx, y); ctx.lineTo(gx, y + s); }
    for (let gy = y; gy <= y + s; gy += step) { ctx.moveTo(x, gy); ctx.lineTo(x + s, gy); }
    ctx.stroke();
  } else if (style.hatch === 'concrete') {
    hatchConcrete(ctx, x, y, x + s, y + s, step, style.line);
  } else {
    hatchGrout(ctx, x, y, x + s, y + s, step, style.line);
  }
  ctx.restore();
  ctx.strokeStyle = style.stroke; ctx.lineWidth = 1;
  ctx.strokeRect(x + 0.5, y + 0.5, s - 1, s - 1);
  ctx.restore();
}
